import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { DEFAULT_PAGING, Filter, OrganizationService } from 'src/app/@shared';
import { environment } from 'src/environments/environment';
import { DEFAULT_DATE_RANGE } from './event.service';
import { v4 as uuidv4 } from 'uuid';
import {
  PrintAttributeDefinition,
  PrintAttributeSavePayload,
} from '../models/print-attribute.model';

/** Server-side list filters for `PrintAttributeDomains` (repeated query keys). */
export interface PrintAttributeListFilters {
  divisionIds: string[];
  attributeTypes: string[];
  eventTypeIds: string[];
}

const EMPTY_LIST_FILTERS: PrintAttributeListFilters = {
  divisionIds: [],
  attributeTypes: [],
  eventTypeIds: [],
};

@Injectable({
  providedIn: 'root',
})
export class PrintAttributeService {
  private dateRangeBehaviorSubject = new BehaviorSubject(DEFAULT_DATE_RANGE);
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 20,
  });
  private sortBehaviorSubject = new BehaviorSubject({
    active: 'Name',
    direction: 'asc' as 'asc' | 'desc',
  });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private viewModeBehaviorSubject = new BehaviorSubject<string>('TABLE');
  private filterBehaviorSubject = new BehaviorSubject<Filter[]>([]);
  private listFiltersBehaviorSubject =
    new BehaviorSubject<PrintAttributeListFilters>({ ...EMPTY_LIST_FILTERS });

  // we do not wish to expose our behavior subjects. create public observables
  public dateRange$ = this.dateRangeBehaviorSubject.asObservable();
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public viewMode$ = this.viewModeBehaviorSubject.asObservable();
  public filters$ = this.filterBehaviorSubject.asObservable();
  public reload$ = this.reloadBehaviorSubject.asObservable();
  public listFilters$ = this.listFiltersBehaviorSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService
  ) {}

  private sameListFilters(
    a: PrintAttributeListFilters,
    b: PrintAttributeListFilters,
  ): boolean {
    const key = (ids: string[]) => [...ids].sort().join('\u0001');
    return (
      key(a.divisionIds) === key(b.divisionIds) &&
      key(a.attributeTypes) === key(b.attributeTypes) &&
      key(a.eventTypeIds) === key(b.eventTypeIds)
    );
  }

  getListFilters(): PrintAttributeListFilters {
    return this.listFiltersBehaviorSubject.getValue();
  }

  /** Applies server-side filters and resets to the first page (same pattern as `search`). */
  setListFilters(filters: PrintAttributeListFilters): void {
    const prev = this.listFiltersBehaviorSubject.getValue();
    if (this.sameListFilters(prev, filters)) {
      return;
    }
    this.listFiltersBehaviorSubject.next(filters);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  clearListFilters(): void {
    this.setListFilters({ ...EMPTY_LIST_FILTERS });
  }

  /**
   * Full `$orderby` property path (before direction). Most fields live under expanded `Detail`;
   * domain-level fields like `DivisionNames` use the root path only.
   */
  private sortOrderByPath(active: string): string | null {
    const map: Record<string, string> = {
      Name: 'Detail/PrintAttributeName',
      PrintAttributeType: 'Detail/PrintAttributeType',
      DivisionNames: 'DivisionNames',
      EventTypeName: 'EventTypeName',
      BannerName: 'BannerName',
      Description: 'Detail/Description',
      Versions: 'Detail/LogicalVersions',
    };
    return map[active] ?? null;
  }

  private buildODataOrderBy(sort: {
    active: string;
    direction: string;
  }): string | null {
    const path = this.sortOrderByPath(sort.active);
    if (!path) {
      return null;
    }
    const dir =
      String(sort.direction || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc';
    return `${path} ${dir}`;
  }

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.pageBehaviorSubject.pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
    this.listFiltersBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([page, sort, search, reload, listFilters]) => {
      // set the query string parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $count: true,
          $expand: 'Detail'
        },
      });

      const orderBy = this.buildODataOrderBy(sort);
      if (orderBy) {
        if(orderBy.includes('Detail/PrintAttributeType')) {
          params = params.set('OrderBy', orderBy);
        } else {
          params = params.set('$orderby', orderBy);
        }
    }

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      listFilters.divisionIds.forEach((id) => {
        params = params.append('divisionIds', id);
      });
      listFilters.attributeTypes.forEach((t) => {
        params = params.append('attributeTypes', t);
      });
      listFilters.eventTypeIds.forEach((id) => {
        params = params.append('eventTypeIds', id);
      });

      return params;
    })
  );

  // sets the sort property and order
  sort(sort: any) {
    this.sortBehaviorSubject.next(sort);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  reload() {
    this.reloadBehaviorSubject.next(uuidv4());
  }

  page(page: any) {
    this.pageBehaviorSubject.next(page);
  }

  // sets the search phrase
  search(search: string) {
    this.searchBehaviorSubject.next(search);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  private printAttributeResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/GetAll`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call
  );

  // print attributes listing
  public printAttributes$: Observable<PrintAttributeDefinition[]> = this.printAttributeResponse$.pipe(
    map((res: any) => res.value)
  );

  public totalRecords$: Observable<number> = this.printAttributeResponse$.pipe(
    map((res: any) => res['@odata.count'] || 0)
  );

  public printAttributeTypes$: Observable<{ id: string; label: string }[]> = this.httpClient.get<string[]>(
    `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/GetPrintAttributeTypes`
  ).pipe(
    map((res: any) => {
      if (Array.isArray(res?.value)) {
        return res.value.map((t: string) => ({
          id: t,
          label: this.normalizePrintAttributeType(t),
        }));
      }
      return [];
    })
  );

  public normalizePrintAttributeType(type: string): string {
    return type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  createPrintAttribute(payload: PrintAttributeSavePayload) {
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains`,
      payload
    );
  }

  getPrintAttributeDefinition(id: string): Observable<PrintAttributeDefinition> {
    return this.httpClient.get<PrintAttributeDefinition>(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/${id}`, { params: new HttpParams().set('$expand', 'Detail') }
    );
  }

  updatePrintAttribute(id: string, payload: PrintAttributeSavePayload) {
    return this.httpClient.put(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/${id}`,
      payload
    );
  }

  deletePrintAttributes(ids: string[]) {
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/BulkDelete`,
      ids
    );
  }

  clonePrintAttributes(ids: string[]) {
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/Clone`,
      ids
    );
  }

  downloadPrintAttributesCSV() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttribute/Export`;
    window.open(url, '_blank');
  }
}
